import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { Input, ListGroup, ListGroupItem, ModalHeader } from 'reactstrap';

import ComponentPositionInput from 'app/PageComponentsList/components/ComponentPositionInput';
import { noop } from 'lib/noop';
import { ClearButton, IconButton, PrimaryButton } from 'ui/Components';
import { search } from 'ui/Icons';
import { makeId } from 'utils/selectors';

import styles from './ComponentsToolbox.css';

/**
 * This screen shows all the available component templates (nui components).
 * The lst of component is supplied as a prop to this component.
 * @param {object} componentsList - List of components to render in toolbox.
 * @param {object} currentComponentSelected - action to be dispatched on component selecton.
 * @returns {React.Component} the toolbox component.
 */
export default class ComponentsToolbox extends Component {
  static propTypes = {
    cancelText: propTypes.string,
    componentsList: propTypes.arrayOf(propTypes.string),
    onCancel: propTypes.func,
    onSelect: propTypes.func,
    pageComponents: propTypes.arrayOf(propTypes.object),
    renderingEnvName: propTypes.string,
    selectText: propTypes.string
  };

  static defaultProps = {
    cancelText: 'Cancel',
    componentsList: [],
    onCancel: noop,
    onSelect: noop,
    pageComponents: [],
    renderingEnvName: '',
    selectText: 'Select'
  };

  constructor(props) {
    super(props);
    this.state = {
      componentsList: props.componentsList,
      selectedComponent: ''
    };

    this.onItemClick = this.onItemClick.bind(this);
    this.filter = this.filter.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (props.componentsList !== state.componentsList) {
      return {
        componentsList: props.componentsList
      };
    }
    return null;
  }

  /**
   * set selectedComponent state
   * @param {string} selectedComponent - selected component variation name
   */
  onItemClick(selectedComponent) {
    this.setState({ selectedComponent });
  }

  /**
   * get styles for a component variation
   * @param {string} variation - component variation name
   * @returns {string} a className
   */
  getListItemClass(variation) {
    const { selectedComponent } = this.state;
    return variation === selectedComponent ? styles.listItemSelected : styles.listItem;
  }

  /**
   * filters an event
   * @param {object} e - the event
   */
  filter(e) {
    const componentsList = this.props.componentsList.filter(el =>
      el.toLowerCase().includes(e.target.value.toLowerCase())
    );
    this.setState({ componentsList });
  }

  render() {
    const {
      selectText,
      cancelText,
      onSelect,
      onCancel,
      renderingEnvName,
      pageComponents
    } = this.props;
    const { componentsList } = this.state;

    return (
      <Fragment>
        <ModalHeader>
          Component Template
          {renderingEnvName && <small>{` (${renderingEnvName})`}</small>}
        </ModalHeader>
        <div className="d-flex flex-column mt-3">
          <div className={styles.wrapper}>
            <Input
              type="text"
              placeholder="Search"
              data-test-id="addnewcomponent-search-input"
              onKeyUp={e => this.filter(e)}
            />
            <IconButton
              className={styles.icon}
              alt="search icon"
              icon={search}
              noMargin
              rem={1.5}
            />
            <ListGroup className={styles.listGroup}>
              {componentsList.map(name => (
                <ListGroupItem
                  key={name}
                  className={this.getListItemClass(name)}
                  onClick={() => this.onItemClick(name)}
                  data-test-id={makeId({
                    component: this.constructor.name,
                    listItem: name
                  })}
                >
                  {name}
                </ListGroupItem>
              ))}
            </ListGroup>
          </div>
          {!!pageComponents.length && (
            <ComponentPositionInput maxLength={pageComponents.length} />
          )}
          <div className={classnames(styles.buttonWrapper, 'pb-3')}>
            <ClearButton
              onClick={() => onCancel()}
              data-test-id="addnewcomponent-cancel-button"
            >
              {cancelText}
            </ClearButton>
            <PrimaryButton
              data-test-id="addnewcomponent-select-button"
              onClick={() => onSelect(this.state.selectedComponent)}
              disabled={!this.state.selectedComponent}
            >
              {selectText}
            </PrimaryButton>
          </div>
        </div>
      </Fragment>
    );
  }
}
